import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "../services/rental.service";
import type {
  RentalSummaryParams,
  RentalSummaryResponseDTO,
} from "../types/rental.types";

interface UseRentalSummaryOptions {
  initialParams: RentalSummaryParams;
}

const ZERO_SUMMARY: RentalSummaryResponseDTO = {
  totalRevenue: 0,
  onStreetCount: 0,
  incompleteCount: 0,
};

export function useRentalSummary({ initialParams }: UseRentalSummaryOptions) {
  const [data, setData] = useState<RentalSummaryResponseDTO>(ZERO_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<RentalSummaryParams>(initialParams);
  const requestIdRef = useRef(0);

  const load = useCallback(async (target: RentalSummaryParams) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await rentalService.summary(target);
      if (requestIdRef.current !== requestId) return;
      setData(response);
    } catch (err: unknown) {
      if (requestIdRef.current !== requestId) return;
      const message = getApiErrorMessage(err, "Erro ao carregar resumo");
      setError(message);
      toast.error(message);
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(params);
  }, [load, params]);

  const reload = useCallback(() => {
    load(params);
  }, [load, params]);

  return {
    data,
    loading,
    error,
    params,
    setParams,
    reload,
  };
}
